import { getFirestore /*, getFirestoreIncrementField*/ } from '../../system/firebase';

import { checkEsToken, elasticSearchEmpJobSearchPostFetch, elasticSearchJsProfileSearchPostFetch, elasticSearchOccupationSkillSetSuggestionSearchPostFetch } from './appServices';
import {
    PAY_FREQUENCY_LIST_BI_WEEKLY,
    PAY_FREQUENCY_LIST_DAILY,
    PAY_FREQUENCY_LIST_HOURLY,
    PAY_FREQUENCY_LIST_MONTHLY,
    PAY_FREQUENCY_LIST_PER_JOB,
    PAY_FREQUENCY_LIST_WEEKLY,
    PAY_FREQUENCY_LIST_YEARLY,
    USER_TYPE_JS,
} from '../constants';
import { mapDefinedFields } from '../dtoHelper';
import { convertFirebaseTimestampsToDate /*, getCombinedDateTime*/ } from '../dateHelper';
import {
    ELASTICSEARCH_PAGE_SIZE,
    // ELASTICSEARCH_SUGGESTION_API_URL,
    ELASTICSEARCH_TYPE_CITY,
    ELASTICSEARCH_TYPE_COUNTRY,
    // ELASTICSEARCH_TYPE_OCCUPATION,
    ELASTICSEARCH_TYPE_STATE,
    mapEmpJobsDataFromElasticSearchJson,
    mapProfilesDataFromElasticSearchJson,
} from '../elasticSearchHelper';

import _get from 'lodash/get';
import _set from 'lodash/set';
import _fpSet from 'lodash/fp/set';
import _map from 'lodash/map';
import _omit from 'lodash/omit';

export const fetchJobs = set => {
    const db = getFirestore();
    const jobsRef = db.collection('jobs');
    return jobsRef.onSnapshot(docSnapshot => {
        set(
            docSnapshot.docs
                .map(doc => {
                    let data = doc.data();
                    data = convertFirebaseTimestampsToDate(data, ['createDt', 'updateDt']);
                    return {
                        ...data,
                        uid: doc.id,
                        // jobSeekers: convertJobSeekersFirebaseTimestampsToDate(_get(data, 'jobSeekers')),
                    };
                })
                .filter(el => el.active),
        );
    });
};

export const fetchUser = async (userId, set) => {
    const db = getFirestore();
    const userRef = db.collection('users').doc(userId);

    const doc = await userRef.get();
    doc.exists && set(doc.data());
};

//--------------------------------------------------------------------------
// Search JS_PROFILE
// queryObject elements:
//--------------------------------------------------------------------------
export const fetchJobSeekerProfiles = async ({ from = 0, size = ELASTICSEARCH_PAGE_SIZE, queryObject, esToken, setEsToken } = {}) => {
    // const db = getFirestore();

    // format the search criteria queryObject
    const jobTypeList = _get(queryObject, 'jobType', []);
    const salaryRate = _get(queryObject, 'salaryRate'); // salaryRate is an object { rateMin, payFrequency }
    const rateMax = _get(salaryRate, 'rateMax', Number.MAX_SAFE_INTEGER); // rateMax will be ignored if payFrequency is not selected
    const payFrequency = _get(salaryRate, 'payFrequency');
    const showOnly = _get(salaryRate, 'showOnly');
    let salaryRateDto = null;
    switch (payFrequency) {
        case PAY_FREQUENCY_LIST_HOURLY: {
            salaryRateDto = { rateYearlyMin: rateMax * 2080 };
            break;
        }
        case PAY_FREQUENCY_LIST_WEEKLY: {
            salaryRateDto = { rateYearlyMin: rateMax * 52 };
            break;
        }
        case PAY_FREQUENCY_LIST_BI_WEEKLY: {
            salaryRateDto = { rateYearlyMin: rateMax * 26 };
            break;
        }
        case PAY_FREQUENCY_LIST_MONTHLY: {
            salaryRateDto = { rateYearlyMin: rateMax * 12 };
            break;
        }
        case PAY_FREQUENCY_LIST_YEARLY: {
            salaryRateDto = { rateYearlyMin: rateMax };
            break;
        }
        case PAY_FREQUENCY_LIST_DAILY: {
            salaryRateDto = { rateDailyMin: rateMax };
            break;
        }
        case PAY_FREQUENCY_LIST_PER_JOB: {
            salaryRateDto = { ratePerJobMin: rateMax };
            break;
        }
        default:
        // do nothing
    }
    showOnly && _set(salaryRateDto, 'payFrequency', payFrequency);
    const location = _get(queryObject, 'location.value'); // location is a label-value pair from suggestion
    const locationType = _get(location, 'type');
    let locationQueryObject = {};
    switch (locationType) {
        case ELASTICSEARCH_TYPE_COUNTRY: {
            locationQueryObject = {
                country: _get(location, 'name'),
            };
            break;
        }
        case ELASTICSEARCH_TYPE_STATE: {
            locationQueryObject = {
                country: _get(location, 'country'),
                state: _get(location, 'name'),
            };
            break;
        }
        case ELASTICSEARCH_TYPE_CITY: {
            const cityLocationLatitude = _get(location, 'location.lat');
            const cityLocationLongitude = _get(location, 'location.lon');
            if (cityLocationLatitude && cityLocationLongitude) {
                _set(locationQueryObject, 'location', _get(location, 'location'));
            } else {
                // if no lat/long, match city name
                _set(locationQueryObject, 'city', _get(location, 'name'));
                _set(locationQueryObject, 'state', _get(location, 'state'));
                _set(locationQueryObject, 'country', _get(location, 'country'));
            }
            break;
        }
        default:
        // do nothing
    }
    //TODO comment this out when we are not loading all talents -el 20221013
    const sortDto = queryObject?.sortTag || undefined;

    // Create the actual queryObject for ElasticSearch
    const mappedQueryObject = mapDefinedFields({
        occupation: queryObject?.occupation || undefined,
        ...locationQueryObject,
        willingRelocate: queryObject?.willingRelocate || undefined,
        employmentType: jobTypeList.length > 0 ? jobTypeList : undefined,
        ...salaryRateDto,
        ...sortDto, //TODO comment this out when we are not loading all talents -el 20221013
        // id: queryObject?.id || undefined,
        isPublic: true,
    });

    // console.log('==========> ', { mappedQueryObject });

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);

        if (checkedEsToken) {
            const searchResult = await elasticSearchJsProfileSearchPostFetch({ queryObject: { ...mappedQueryObject, from, size }, token: checkedEsToken.token });

            if (_get(searchResult, 'success')) {
                const total = _get(searchResult, 'total', 0);
                const result = _get(searchResult, 'result', []);
                const mappedList = _map(result, jobItem => {
                    return mapProfilesDataFromElasticSearchJson(jobItem);
                });
                return { total, list: mappedList };
            } else {
                console.error('Failed to search talents');
            }

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }
        }
    } catch (error) {
        console.error('Failed checking token', error);
    }

    return { total: 0, list: [] };
};

export const loadJobSeekerProfiles = ({ setTalents } = {}) => {
    const db = getFirestore();

    const profilesRef = db.collection('profiles').where('isPublic', '==', true);

    return profilesRef.onSnapshot(querySnapshot => {
        const profileList = _map(querySnapshot.docs, doc => {
            const data = doc.data();

            const dto = convertFirebaseTimestampsToDate(data, ['updateDt', 'createDt']);

            return { ...dto, uid: doc.id };
        });

        // console.log('loadJobSeekerProfiles', JSON.stringify({ profileList }));
        setTalents(profileList);
    });
};

// DEPRECATED
export const fetchAllUserProfiles = async set => {
    const db = getFirestore();
    //TODO Temporarily fetch all users until we implement ElasticSearch
    const allUserProfiles = [];
    await db
        .collection('profiles')
        .where('isPublic', '==', true)
        .get()
        .then(querySnapshot => {
            querySnapshot.forEach(doc => {
                let data = doc.data();

                data = convertFirebaseTimestampsToDate(data, ['createDt', 'updateDt']);
                const resumeList = _get(data, 'resumeList');
                const resumeListMappedDates = _map(resumeList, resume => {
                    return convertFirebaseTimestampsToDate(resume, ['updateDt']);
                });
                data = _fpSet('resumeList', resumeListMappedDates, data);

                allUserProfiles.push({ id: doc.id, data });
            });
        })
        .catch(error => {
            console.log('Error getting User profiles: ', error);
        });
    set(allUserProfiles);
};

export const loadStatesList = async (setStatesList, country) => {
    const db = getFirestore();

    const statesRef = db.doc(`app/lovs/countries/${country}`);

    const doc = await statesRef.get();
    doc.exists && setStatesList(doc.data().stateList.map(s => s.name));
};
export const loadOccupationsList = async setOccupationsList => {
    console.log('loading occupation list');
    const db = getFirestore();
    const statesRef = db.doc(`app/lovs/`);

    const doc = await statesRef.get();
    doc.exists && setOccupationsList(doc.data().occupationList.map(s => s));
};

// DEPRECATED
export const saveToRecentSearches = async ({ user, userId, query, flush }) => {
    console.log({ user, userId, query, flush });
    const db = getFirestore();
    const userRef = db.collection('users').doc(userId);

    try {
        let recentSearches = [];
        if (!flush) {
            recentSearches = [..._get(user, 'recentSearches', [])];
            recentSearches.push(query);
            if (recentSearches.length > 5) {
                recentSearches.shift();
            }
        }

        // if (!user.recentSearches) user.recentSearches = [];

        // let { recentSearches } = user;

        // if (recentSearches.length < 5) {
        //     user.recentSearches.push(query);
        // } else {
        //     let [toPop, ...rest] = recentSearches;
        //     user.recentSearches = [...rest, query];
        //     // console.log(toPop);
        // }

        // if (flush) {
        //     user.recentSearches = [];
        // }

        // await userRef.update(user);

        await userRef.update({ recentSearches });
    } catch (error) {
        console.log('Talent Center saveToRecentSearches failed', error);
    }
};

export const searchJobSeekers = ({ user, userId, query, flush }) => {
    // console.log({ user, userId, query, flush });
    // const db = getFirestore();

    try {
        //TODO Implement ElasticSearch
        alert('Future Elastic Search job seekers');

        // save the search to user's recent searches
        saveToRecentSearches({ user, userId, query, flush });
    } catch (error) {
        console.log('Talent Center searchJobSeekers failed', error);
    }
};

//--------------------------------------------------------------------------
// Employer Active Jobs Suggestion
//--------------------------------------------------------------------------
export const getEmployerActiveJobsSuggestions = async ({ keywords, employerId, esToken, setEsToken }) => {
    // const db = getFirestore();

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);
    } catch (error) {
        console.log('Failed checking token', error);
    }

    try {
        if (checkedEsToken) {
            const esResp = await elasticSearchEmpJobSearchPostFetch({
                // url: ELASTICSEARCH_SEARCH_EMP_JOB_API_URL,
                // type: ELASTICSEARCH_TYPE_OCCUPATION,
                queryObject: mapDefinedFields({ jobTitle: keywords, active: true, parentId: employerId }),
                token: checkedEsToken.token,
            });

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }

            // const result = _get(esResp, 'result', []);
            // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> ', { result });

            // return result;

            if (_get(esResp, 'success')) {
                // const result = _get(esResp, 'result', []);
                const result = _get(esResp, 'result', []);
                const mappedList = _map(result, empItem => mapEmpJobsDataFromElasticSearchJson(empItem));
                // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> ', { mappedList });
                return mappedList;
            } else {
                console.log('ElasticSearch failed to fetch employer active jobs suggestions.');
                return [];
            }
        }
    } catch (error) {
        console.log('Transaction failed: ', error);
        return [];
    }
};

export const loadActiveJobOptions = ({ employerId, setList }) => {
    const db = getFirestore();

    const jobsRef = db.collection('jobs').where('employerId', '==', employerId).where('active', '==', true);

    return jobsRef.onSnapshot(querySnapshot => {
        const jobList = _map(querySnapshot.docs, doc => {
            const data = doc.data();

            const dto = convertFirebaseTimestampsToDate(data, ['updateDt', 'createDt', 'statusDt']);

            return { ...dto, uid: doc.id };
        });

        // console.log('loadActiveJobOptions', JSON.stringify({ jobList }));
        setList(jobList);
    });
};

export const fetchEmployerActiveJobs = (set, employerId) => {
    const db = getFirestore();
    const empJobsQuery = db.collection('myJobsEmp').doc(employerId).collection('empJobs').where('active', '==', true);

    return empJobsQuery.onSnapshot(querySnapshot => {
        const employerJobs = [];
        querySnapshot.forEach(doc => {
            const jobId = doc.id;
            const empJobData = doc.data();

            if (_get(empJobData, 'active')) {
                employerJobs.push({ ...empJobData, uid: jobId });
            }
        });
        set(employerJobs);
    });
};

export const getJobSeekers = set => {
    const db = getFirestore();
    const jobSeekersRef = db.collection('users').where('type', '==', USER_TYPE_JS);
    return jobSeekersRef.onSnapshot(docSnapshot => {
        set(docSnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }))); //Do not expect the document to have uid; doc id is uid so append it.
    });
};

export const loadConversations = (employerId, setMyMessagesData) => {
    const db = getFirestore();
    const threadSummariesQuery = db.collection(`myMessages/${employerId}/threadSummaries`).where('active', '==', true).orderBy('lastMessageDt', 'desc');
    const threadSummaries = [];
    return threadSummariesQuery.onSnapshot(threadSummariesSnapshot => {
        threadSummariesSnapshot.forEach(doc => {
            threadSummaries.push(doc.data());
        });
        setMyMessagesData(threadSummaries);
    });
};

//--------------------------------------------------------------------------
// Occupation Suggestion
//--------------------------------------------------------------------------
export const getOccupationSuggestions = async ({ keywords, esToken, setEsToken }) => {
    // const db = getFirestore();

    let checkedEsToken = null;
    try {
        checkedEsToken = await checkEsToken(esToken);
    } catch (error) {
        console.log('Failed checking token', error);
    }

    try {
        if (checkedEsToken) {
            // const esResp = await getElasticSearchSuggestionPostFetch({
            //     url: ELASTICSEARCH_SUGGESTION_API_URL,
            //     type: ELASTICSEARCH_TYPE_OCCUPATION,
            //     queryObject: { name: keywords },
            //     token: checkedEsToken.token,
            // });
            const esResp = await elasticSearchOccupationSkillSetSuggestionSearchPostFetch({
                queryObject: { name: keywords },
                token: checkedEsToken.token,
            });

            if (checkedEsToken.isNew) {
                setEsToken(_omit(checkedEsToken, 'isNew'));
            }

            const result = _get(esResp, 'result', []);
            // console.log('>>>>>>>>>>>>>>>>>>>>>>>>>>>> ', { result });

            return _map(result, item => item?.name);
        }

        return [];
    } catch (error) {
        console.log('Transaction failed: ', error);
    }
};

export const loadRecentSearches = ({ userId, setData }) => {
    const db = getFirestore();

    const talentCenterRef = db.collection('talentCenters').doc(userId);
    let recentSearches = [];

    return talentCenterRef.onSnapshot(doc => {
        if (doc.exists) {
            const data = doc.data();
            recentSearches = _get(data, 'recentSearches', []);
        }
        setData(recentSearches);
    });
};

export const updateRecentSearches = async ({ userId, recentSearches = [] }) => {
    const db = getFirestore();

    try {
        const talentCenterRef = db.collection('talentCenters').doc(userId);
        talentCenterRef.set({ recentSearches }, { merge: true });
    } catch (error) {
        console.log('Failed to update recent searches: ', error);
    }
};

export const fetchSavedJobSeekersByEmployer = async ({ jsIdList, employerId } = {}) => {
    const db = getFirestore();

    const savedJobSeekersQuery = db
        .collection('savedJobSeekers')
        .where('empId', '==', employerId)
        .where('jsId', 'in', [...jsIdList])
        .orderBy('createDt', 'desc');
    let savedJobSeekers = [];

    const savedJobSeekersSnapshots = await savedJobSeekersQuery.get();

    savedJobSeekersSnapshots.forEach(doc => {
        savedJobSeekers.push(convertFirebaseTimestampsToDate(doc.data(), ['createDt']));
    });

    return savedJobSeekers;
};
